// RolesList.jsx - View-only table
import React, { useState, useEffect } from 'react';
import { Table, Spinner } from 'react-bootstrap';
import { 
  Shield, 
  Search, 
  CheckCircle,
  Clock,
  AlertCircle
} from 'lucide-react';
import { roleService } from '../../services';
import './RolesList.css';

const RolesList = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    setLoading(true);
    setError('');
    const result = await roleService.getRoles();
    if (result.success) {
      setRoles(result.data || []);
    } else {
      setError(result.message || 'Failed to load roles');
    }
    setLoading(false);
  };

  const filteredRoles = roles.filter(role =>
    role.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusConfig = (role) => {
    const isActive = role.is_active !== undefined ? role.is_active : true;
    const configs = {
      active: { color: '#10B981', bg: '#D1FAE5', label: 'Active', icon: CheckCircle },
      inactive: { color: '#94A3B8', bg: '#F1F5F9', label: 'Inactive', icon: Clock }
    };
    return isActive ? configs.active : configs.inactive;
  };

  const getRoleBadge = (roleName) => {
    const styles = {
      super_admin: { background: '#EDE9FE', color: '#4C1D95' },
      company_admin: { background: '#DBEAFE', color: '#1E3A8A' },
      club_admin: { background: '#DBEAFE', color: '#1E3A8A' },
      club_staff: { background: '#E0F2FE', color: '#0369A1' },
      club_viewer: { background: '#F1F5F9', color: '#64748B' }
    };
    return styles[roleName?.toLowerCase()] || styles.club_viewer;
  };

  return (
    <div className="roles-list">
      {/* Error Alert */}
      {error && (
        <div className="roles-list-error">
          <AlertCircle size={18} />
          <span>{error}</span>
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* Search */}
      <div className="roles-list-search-container">
        <div className="roles-list-search-wrapper">
          <Search size={18} className="roles-list-search-icon" />
          <input
            type="text"
            placeholder="Search roles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="roles-list-search-input"
          />
        </div>
        <div className="roles-list-count">
          <Shield size={14} />
          {filteredRoles.length} role{filteredRoles.length !== 1 ? 's' : ''} found
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="roles-list-loading-state">
          <Spinner animation="border" className="roles-list-loading-spinner" />
          <p>Loading roles...</p>
        </div>
      ) : filteredRoles.length === 0 ? (
        <div className="roles-list-empty-state">
          <Shield size={48} className="roles-list-empty-icon" />
          <p className="roles-list-empty-title">No roles found</p>
          <p className="roles-list-empty-subtitle">
            {searchTerm ? 'Try adjusting your search' : 'No roles in the system yet'}
          </p>
        </div>
      ) : (
        <div className="roles-list-table-wrapper">
          <Table hover className="roles-list-table">
            <thead>
              <tr className="roles-list-table-header">
                <th>Role</th>
                <th>Description</th>
                <th>Users</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {filteredRoles.map((role) => {
                const statusConfig = getStatusConfig(role);
                const StatusIcon = statusConfig.icon;
                const roleBadge = getRoleBadge(role.name);
                return (
                  <tr key={role.uuid || role.id} className="roles-list-table-row">
                    <td>
                      <div className="roles-list-role-cell">
                        <div className="roles-list-role-avatar" style={{ background: roleBadge.background, color: roleBadge.color }}>
                          <Shield size={16} />
                        </div>
                        <span className="roles-list-role-name">{role.name?.replace('_', ' ') || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="roles-list-role-description">{role.description || '—'}</td>
                    <td>
                      <span className="roles-list-user-count">{role.user_count || 0}</span>
                    </td>
                    <td>
                      <span className="roles-list-status-badge" style={{ 
                        background: statusConfig.bg, 
                        color: statusConfig.color 
                      }}>
                        <StatusIcon size={12} />
                        {statusConfig.label}
                      </span>
                    </td>
                    <td className="roles-list-created-at">
                      {role.created_at ? new Date(role.created_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default RolesList;